import RestCrudThunks from './restCrudThunksGenerator';
import FetchUtils, { parseJSON } from './fetch_utils';
import { getBackendUri } from '../utils/EnvConfigUtils';
import { logoutSuccess } from '../actions/session_actions';
import { nullSafeGetOrElse } from '../utils/DataAccessUtils';
import { DEFAULT_NAME_SORTER, ROLE_TYPES } from '../utils/DataConstants';
import { USER_GROUPS_CRUD_ACTION_CREATORS } from '../actions/user_groups_actions';
import {
	USER_GROUPS_FOR_MEMBER_TARGET_COLLECTION,
	USER_GROUPS_PAGE_SIZE,
	USER_GROUPS_PICKER_TARGET_COLLECTION,
} from '../types/user_groups';

// ---------------------------------------------------------------------------
// User group CRUD. Contract read from the backend branch `palani/ope-5239`,
// commit "Rework user groups onto the standard CRUD framework" — the controller now extends
// BuyerSupplierCRUDHelperWithFingerPrint and mirrors WorkOrderLabelController, so the plain
// generator matches it exactly and no hand-written thunk is needed.
//
//   GET     /api/v1/buyer/admin/user_groups            (offset/limit/sort_by/order all work)
//   POST    /api/v1/buyer/admin/user_groups
//   PUT     /api/v1/buyer/admin/user_groups            (id in the BODY — no :id segment)
//   GET     /api/v1/buyer/admin/user_groups/count_by
//   GET     /api/v1/buyer/admin/user_groups/:id
//   DELETE  /api/v1/buyer/admin/user_groups/:id        (soft delete, inherited markDelete)
//
// History worth keeping, because each of these cost a debugging round:
//  * The prefix is `admin/`. `/buyer/user/user_groups` and `/buyer/user_groups` both 404 —
//    those came from the superseded 2023 `ope-823` branch, which is not the contract.
//  * PUT used to take the id in the path and now takes it in the body. That is the standard
//    framework shape, so `update()` is correct and a bespoke PUT-to-/:id thunk 404s.
//  * The membership field is `memberEmails`, read with `optValueSeq`, so a wrong key is NOT
//    an error — it resolves to an empty Seq and the update removes every existing member.
//    A misnamed field silently empties the group instead of failing.
//
// Writable fields are exactly `id` (optional), `name` (required) and `memberEmails`
// (optional). The tenant is forced from the authenticated contact in `writeResult`, so
// client-supplied `buyerCompanyId` / `supplierFacilityId` are ignored. The counts and
// createdBy/updatedBy are server-derived, and the service hydrates `memberEmails` plus the
// counts on the list, the single-GET and every write response.
//
// Error responses worth surfacing:
//   409 UserGroupAlreadyExistsException — duplicate group name within the tenant
//   400 InvalidUserGroupException      — empty name, or a member email outside the tenant
//   404                                — group missing or owned by another tenant
//
// Tenancy: buyer groups are COMPANY-wide (`buyerCompanyId`), so a member may be any user in
// any facility of the company; supplier groups are scoped to the single supplier facility.
// Either way the tenant is server-forced, so the client never sends it.
//
// The surface is mirrored under `/api/v1/supplier/admin/user_groups` by
// SupplierUserGroupController (gated to SUPPLIER_ADMIN / SUPER_ADMIN instead of BUYER_ADMIN),
// hence the role-parameterised export below.
//
// There is also `PUT /admin/{role}/users/:userName/user_groups` (body `{ userGroupIds }`),
// which sets one user's memberships from the user side — see `syncUserGroupsForUser` below.
// The invite / edit user modals must call it after their contact write: `updateContact` and the
// sign-up invite ignore `userGroupIds`, so sending it there saves nothing. Likewise the contact
// GET does not hydrate a user's groups, which is why the member read below exists.
// ---------------------------------------------------------------------------

export const userGroupsRestCrudThunksForBuyer = new RestCrudThunks(
	'user_groups',
	'user_group',
	'userGroup',
	'userGroups',
	USER_GROUPS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.BUYER,
	false,
	'admin/'
);

export const userGroupsRestCrudThunksForSupplier = new RestCrudThunks(
	'user_groups',
	'user_group',
	'userGroup',
	'userGroups',
	USER_GROUPS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.SUPPLIER,
	false,
	'admin/'
);

// Consumers pick by the session's runtime role (via `withUserType`) rather than importing a
// role-bound export — dispatching the wrong side would silently hit the other role's endpoint.
export const userGroupsRestCrudThunks = (userType) =>
	userType === ROLE_TYPES.SUPPLIER
		? userGroupsRestCrudThunksForSupplier
		: userGroupsRestCrudThunksForBuyer;

// The invite / edit user modals mount the User Groups field TWICE when an existing user is
// opened: the form body renders once, the user fetch then raises a <Spin/> that unmounts it, and
// it mounts again when that clears. `getTakeLatest` answers the second read by CANCELLING the
// first, so without this the data would not start arriving until the user fetch had finished.
//
// Coalescing keeps the request that started when the modal opened: a call made while an
// identical one is still in flight gets that promise instead of issuing a second request. The
// entry is dropped the moment the request settles, so this is a request-lifetime dedupe and
// never a cache — reopening the modal always refetches.
const requestsInFlight: { [key: string]: Promise<any> } = {};

const coalesced = (key: string, startRequest: () => Promise<any>): Promise<any> => {
	if (!requestsInFlight[key]) {
		const request = startRequest();
		requestsInFlight[key] = request;
		const forget = () => delete requestsInFlight[key];
		request.then(forget, forget);
	}
	return requestsInFlight[key];
};

// Every group in the tenant, for the User Groups picker in the invite / edit user modals.
// `search` is the DAO's name-LIKE filter, so a tenant with more groups than one page can still
// reach the rest by typing.
export const fetchUserGroupsForPicker =
	(userType) =>
	(search = null) =>
	(dispatch) =>
		coalesced(`picker:${userType}:${search || ''}`, () =>
			dispatch(
				userGroupsRestCrudThunks(userType).read(
					search ? { search } : {},
					USER_GROUPS_PICKER_TARGET_COLLECTION,
					{ current: 1, pageSize: USER_GROUPS_PAGE_SIZE },
					DEFAULT_NAME_SORTER,
					null
				)
			)
		);

// The groups one user is currently in. `memberEmail` is an exists-subquery on
// user_group_members, so this is the read that tells the picker what to pre-select.
//
// Lowercased because membership rows are stored normalized (`_.toLowerCase.trim` on every
// write) and the DAO compares the filter with `===`, not a case-insensitive match.
//
// It goes in as a FILTER, not a bare query param — both end up in the query string either way,
// but only a filter is recorded on the target collection, and the picker reads it back to know
// whose membership the collection currently holds.
export const fetchUserGroupsForMember = (userType) => (email) => (dispatch) => {
	const memberEmail = (email || '').toLowerCase().trim();
	return coalesced(`member:${userType}:${memberEmail}`, () =>
		dispatch(
			userGroupsRestCrudThunks(userType).read(
				{},
				USER_GROUPS_FOR_MEMBER_TARGET_COLLECTION,
				{ current: 1, pageSize: USER_GROUPS_PAGE_SIZE },
				DEFAULT_NAME_SORTER,
				{ memberEmail }
			)
		)
	);
};

const fetchUtils = new FetchUtils();

// Makes `userGroupIds` the user's exact memberships within the caller's tenant (groups missing
// from the list are left, so `[]` removes the user from every group). Call it after the invite /
// edit user contact write, which ignores the field.
//
// `undefined` means the User Groups field was not on the form (non-admin, or the feature flag is
// off), so there is nothing to save and no request is made — sending `[]` there would strip the
// user's memberships.
export const syncUserGroupsForUser = (userType) => (email, userGroupIds) => (dispatch) => {
	if (userGroupIds === undefined) {
		return Promise.resolve(null);
	}
	// Membership rows are stored lowercased and trimmed; see fetchUserGroupsForMember.
	const userName = encodeURIComponent((email || '').toLowerCase().trim());
	return new Promise((resolve, reject) => {
		fetchUtils
			.put(`${getBackendUri()}/api/v1/${userType}/admin/users/${userName}/user_groups`, {
				headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
				data: { userGroupIds: userGroupIds || [] },
			})
			.then(parseJSON)
			.then(({ json, status }) => {
				if (status === 401) {
					dispatch(logoutSuccess());
					reject('unauthorized');
					return;
				}
				if (!json || json.status === 'error' || status >= 400) {
					reject(nullSafeGetOrElse('message', json, 'Could not update user groups'));
					return;
				}
				resolve(json.data);
			})
			.catch((error) => reject(error && error.message));
	});
};
